import React from 'react';
import {
  DEFAULT_SITE_VIEW,
  SITE_SORT_OPTIONS,
  type PortfolioSiteViewState,
  type buildSiteListOptions,
  type selectSiteList,
} from '../../lib/portfolio/siteListView';
import './twoAct.css';

export interface PortfolioSiteListContextValue {
  state: PortfolioSiteViewState;
  onChange: React.Dispatch<React.SetStateAction<PortfolioSiteViewState>>;
  items: ReturnType<typeof selectSiteList>;
  options: ReturnType<typeof buildSiteListOptions>;
  totalCount: number;
  search: string;
  onSearch: (value: string) => void;
  onReset: () => void;
  scopeFiltersActive?: boolean;
}

/** One host-owned view for both the portfolio deck and its site sidebar. */
export const PortfolioSiteListContext = React.createContext<PortfolioSiteListContextValue | null>(null);
export function usePortfolioSiteList() {
  return React.useContext(PortfolioSiteListContext);
}

export function PortfolioSiteListControls({ compact = false }: { compact?: boolean }) {
  const view = usePortfolioSiteList();
  const id = React.useId();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  if (!view) return null;
  const selectedFilters = view.options.filter(({ key }) => view.state[key] !== DEFAULT_SITE_VIEW[key]);
  const activeFilters = selectedFilters.length + (view.scopeFiltersActive ? 1 : 0);
  const canReset = activeFilters > 0 || view.search.length > 0 || view.state.sort !== DEFAULT_SITE_VIEW.sort || view.scopeFiltersActive === true;
  return <div className={`portfolio-view-controls${compact ? ' is-compact' : ''}`}>
    <div className="portfolio-view-main">
      <label className="portfolio-view-field portfolio-view-search" htmlFor={`${id}-search`}>
        <span>Search sites</span>
        <input id={`${id}-search`} type="search" value={view.search} placeholder="Name or location" onChange={event => view.onSearch(event.target.value)} />
      </label>
      <label className="portfolio-view-field portfolio-view-sort" htmlFor={`${id}-sort`}>
        <span>Sort sites</span>
        <span className="portfolio-view-select">
          <select id={`${id}-sort`} value={view.state.sort} onChange={event => {
            const selected = SITE_SORT_OPTIONS.find(option => option.value === event.target.value);
            if (selected) view.onChange(current => ({ ...current, sort: selected.value }));
          }}>
            {SITE_SORT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <svg className="portfolio-view-select-chevron" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
        </span>
      </label>
      <button type="button" className="portfolio-view-filter-toggle" aria-expanded={filtersOpen} aria-controls={`${id}-filters`} onClick={() => setFiltersOpen(open => !open)}>
        <span>Filters</span>
        {activeFilters > 0 && <span className="portfolio-view-filter-badge">{activeFilters}</span>}
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
      </button>
    </div>
    <div id={`${id}-filters`} className="portfolio-view-filters" hidden={!filtersOpen}>
      <div className="portfolio-view-filter-grid">
        {view.options.map(({ key, label, options }) => <label key={key} className="portfolio-view-field" htmlFor={`${id}-${key}`}>
          <span>{label}</span>
          <span className="portfolio-view-select">
            <select id={`${id}-${key}`} value={view.state[key]} onChange={event => {
              const selected = options.find(option => option.value === event.target.value);
              if (selected) view.onChange(current => ({ ...current, [key]: selected.value }));
            }}>
              {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <svg className="portfolio-view-select-chevron" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
          </span>
        </label>)}
      </div>
    </div>
    {(selectedFilters.length > 0 || view.search.length > 0 || view.scopeFiltersActive) && <div className="portfolio-view-chips" aria-label="Active site filters">
      {view.search.length > 0 && <button type="button" className="portfolio-view-chip" aria-label="Remove search filter" onClick={() => view.onSearch('')}><span>Search: {view.search}</span><span aria-hidden="true">×</span></button>}
      {selectedFilters.map(({ key, label, options }) => {
        const selectedLabel = options.find(option => option.value === view.state[key])?.label ?? view.state[key];
        return <button key={key} type="button" className="portfolio-view-chip" aria-label={`Remove ${label} filter: ${selectedLabel}`} onClick={() => view.onChange(current => ({ ...current, [key]: DEFAULT_SITE_VIEW[key] }))}><span>{label}: {selectedLabel}</span><span aria-hidden="true">×</span></button>;
      })}
      {view.scopeFiltersActive && <button type="button" className="portfolio-view-chip" aria-label="Clear additional scope filters" onClick={view.onReset}><span>Additional scope filters</span><span aria-hidden="true">×</span></button>}
    </div>}
    <div className="portfolio-view-meta">
      <p className="portfolio-view-count" role="status">{view.items.length} of {view.totalCount} sites</p>
      <button type="button" className="portfolio-view-reset" onClick={view.onReset} disabled={!canReset}>Reset</button>
    </div>
  </div>;
}
